import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError, map } from 'rxjs/operators';
import { TaskDetailModalComponent } from '../task-detail-modal/task-detail-modal.component';
import {
  EntityPickerComponent,
  PickerOption,
  PickerSearchPage,
} from '../../../shared/components/form/entity-picker/entity-picker.component';
import { TaskFiltersComponent } from '../shared/task-filters/task-filters.component';
import { TaskDrawerService } from '../services/task-drawer.service';
import { TaskApiService } from '../services/task-api.service';
import { TaskCatalogApiService } from '../services/task-catalog-api.service';
import { TaskBulkApiService } from '../services/task-bulk-api.service';
import { TaskErrorService } from '../services/task-error.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { PermissionService } from '../../../shared/services/permission.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { DealApiService } from '../../deals/services/deal-api.service';
import { LeadApiService } from '../../leads/services/lead-api.service';
import { UserDirectoryApiService, directoryUserLabel } from '../../../shared/services/user-directory-api.service';
import {
  TaskResponse,
  TaskFilterRequest,
  TaskTypeResponse,
  TaskStatusResponse,
  TaskPriorityResponse,
  TaskCategoryResponse,
  TaskCancelReasonResponse,
  TaskBulkRequest,
  TaskBulkAction,
  QuickFilter,
  TaskFilterEvent,
} from '../models/task.model';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-task-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TaskDetailModalComponent,
    EntityPickerComponent,
    TaskFiltersComponent,
    HasPermissionDirective,
  ],
  templateUrl: './tareas-list.component.html',
})
export class TareasListComponent implements OnInit, OnDestroy {
  titulo = 'Listado de Tareas';

  // Datos
  tasks: TaskResponse[] = [];
  loading = false;
  error: string | null = null;

  // INC 34 — selector de estado por tarea (acciones rápidas).
  statusMenuTaskId: number | null = null;
  statusMenuPosition: { top: number; left: number } = { top: 0, left: 0 };
  
  // Catálogos
  types: TaskTypeResponse[] = [];
  statuses: TaskStatusResponse[] = [];
  priorities: TaskPriorityResponse[] = [];
  categories: TaskCategoryResponse[] = [];
  cancelReasons: TaskCancelReasonResponse[] = [];

  quickFilter: QuickFilter = 'all';
  currentFilter: TaskFilterRequest = {};

  // Paginación
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 20;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  // Selección
  allSelected = false;
  // Cambiamos a Map para almacenar el objeto completo de la tarea, permitiendo validaciones cruzadas de páginas
  // y acceso a propiedades como intIdUserOwner para la validación de reasignación.
  selectedTasksMap = new Map<number, TaskResponse>();

  // Detail modal state
  showDetailModal = false;
  detailTaskId?: number;

  // Bulk action UI state
  showCancelDialog = false;
  showSnoozeDialog = false;
  bulkCancelReasonId?: number;
  showReassignDialog = false; // Nuevo: para el diálogo de reasignación masiva
  bulkNewOwner: PickerOption | null = null; // Nuevo: usuario seleccionado para reasignación
  hasCompletedTerminalStatus = false;
  reassignWarningMessage: string | null = null; // Nuevo: mensaje de advertencia si el dueño es el mismo
  bulkCancelNotes = '';
  bulkSnoozeDate = '';
  bulkSnoozePreset: 'TOMORROW' | 'IN_3_DAYS' | 'NEXT_WEEK' | 'CUSTOM' =
    'TOMORROW';
  /** Si tiene valor, el dialog de cancelación opera sobre esa tarea (single); si es null, es bulk. */
  cancelTargetTaskId: number | null = null;
  cancelTargetTitle = '';

  // INC 30 — reasignación individual (motivo opcional).
  bulkReassignReason = ''; // motivo compartido por el diálogo de reasignación (single + bulk)
  reassignTargetTaskId: number | null = null; // si tiene valor, el diálogo reasigna esa tarea (single)
  reassignTargetTitle = '';

  // INC 26 — nota opcional al completar (single).
  showCompleteDialog = false;
  completeTargetTaskId: number | null = null;
  completeTargetTitle = '';
  completionNotes = '';

  // Cache de nombres por id (para mostrar el nombre real en chips)
  private readonly companyNames = new Map<number, string>();
  private readonly dealNames = new Map<number, string>();
  private readonly leadNames = new Map<number, string>();
  private readonly ownerNames = new Map<number, string>();
  // INC 12 — títulos de tareas padre (para mostrar "Subtarea de …" en filas de subtareas).
  private readonly parentTaskTitles = new Map<number, string>();

  private readonly destroy$ = new Subject<void>();

  // Search functions para reasignación masiva (no son filtros, son acciones)
  searchUser = (term: string, page: number) => this.searchUsersFn(term, page);

  // Mapeo de acciones masivas a nombres amigables
  private readonly friendlyActionNames: { [key in TaskBulkAction]: string } = {
    COMPLETE: 'Completar',
    CANCEL: 'Cancelar',
    SNOOZE: 'Posponer',
    REASSIGN: 'Reasignar', // Añadido para el incidente 9
    SET_PRIORITY: 'Establecer prioridad',
    DELETE: 'Eliminar',
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private taskApi: TaskApiService,
    private catalogApi: TaskCatalogApiService,
    private bulkApi: TaskBulkApiService,
    private taskErrorService: TaskErrorService,
    private authService: AuthService,
    private drawerService: TaskDrawerService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private leadApi: LeadApiService,
    private userDirectory: UserDirectoryApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private permissions: PermissionService,
  ) {}

  /** INC 34 — solo quien puede editar ve el selector de estado interactivo. */
  get canEditTasks(): boolean {
    return this.permissions.canEdit('tasks');
  }

  ngOnInit(): void {
    this.loadCatalogs();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadTasks());

    // Pre-cargar nombre del usuario actual en el caché para ahorrar peticiones
    const cur = this.authService.getCurrentUser();
    if (cur?.id) {
      this.ownerNames.set(
        cur.id,
        this.formatUserName(cur.fullname, cur.lastname),
      );
    }

    // Al llegar con ?openTask=<id> (desde una notificación in-app) se abre el modal
    // de DETALLE sobre la lista. Antes la notificación navegaba a /tareas/editar/:id
    // y metía al usuario directo al formulario de edición.
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const raw = params.get('openTask');
        if (raw == null) return;
        const id = Number(raw);
        if (Number.isNaN(id)) return;
        this.abrirDetalle(id);
      });
  }

  get canBulkReassign(): boolean {
    const user = this.authService.getCurrentUser();
    return (
      user?.roleName === 'Admin' ||
      user?.roleName === 'Super Admin' ||
      user?.roleName === 'Manager'
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.statusMenuTaskId = null;
  }

  // ===== Carga =====

  private loadCatalogs(): void {
    this.catalogApi
      .getActiveTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (d) => (this.types = d), error: () => {} });
    this.catalogApi
      .getActiveStatuses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (d) => {
          this.statuses = d;
          this.hasCompletedTerminalStatus = d.some(
            (s) => s.isCompletedTerminal,
          );
        },
        error: () => {
          this.hasCompletedTerminalStatus = false;
        },
      });
    this.catalogApi
      .getActivePriorities()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (d) => (this.priorities = d), error: () => {} });
    this.catalogApi
      .getActiveCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (d) => (this.categories = d), error: () => {} });
    this.catalogApi
      .getActiveCancelReasons()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (d) => (this.cancelReasons = d), error: () => {} });
  }

  loadTasks(): void {
    this.loading = true;
    this.error = null;

    // INC 24/38 — orden único compuesto de la tabla: urgentes primero y, dentro de cada
    // nivel de prioridad, lo más recientemente modificado (tp.level ASC NULLS LAST,
    // t.datetime_updated DESC NULLS LAST). sortDirection se ignora para este valor.
    // Manda sobre cualquier sortBy heredado del filtro.
    const finalReq: TaskFilterRequest = {
      ...this.currentFilter,
      page: this.globalPage,
      size: this.pageSize,
      sortBy: 'priority_then_recent',
    };

    this.taskApi
      .filterTasks(finalReq)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.tasks = this.splitTasks(page.content) || [];
          this.globalTotalElements = page.totalElements ?? this.tasks.length;
          this.globalTotalPages = page.totalPages ?? 1;
          this.refreshAllSelected();
          this.loading = false;
          this.resolveAssociations();
        },
        error: () => {
          this.tasks = [];
          this.error = 'No se pudieron cargar las tareas.';
          this.loading = false;
        },
      });
  }

  splitTasks(tasks: TaskResponse[]): TaskResponse[] {
    const taskMap = new Map<number, TaskResponse>();

    tasks.forEach((task) => {
      task.isOpen = false;
      task.subtasks = [];
      taskMap.set(task.id, task);
    });

    const rootTasks: TaskResponse[] = [];

    tasks.forEach((task) => {
      if (!task.idParentTask) {
        rootTasks.push(task);
        return;
      }

      const parent = taskMap.get(task.idParentTask);

      if (parent) {
        parent.subtasks!.push(task);
      }
    });

    return rootTasks;
  }
  /**
   * Para cada tarea, las asociaciones FK (intIdCompany/Deal/Lead) sólo vienen como ID.
   * Recolectamos los IDs únicos no cacheados y los resolvemos en batch para mostrar el nombre real.
   */
  private resolveAssociations(): void {
    const businessId = this.authService.getCurrentUser()?.businessId ?? 1;

    // INC 12 — resolver títulos de tareas padre. Primero desde la página actual (sin red),
    // luego pedir los que falten.
    const parentIdsToFetch = new Set<number>();
    for (const t of this.tasks) {
      if (t.idParentTask == null || this.parentTaskTitles.has(t.idParentTask))
        continue;
      const inPage = this.tasks.find((x) => x.id === t.idParentTask);
      if (inPage) this.parentTaskTitles.set(t.idParentTask, inPage.title);
      else parentIdsToFetch.add(t.idParentTask);
    }
    for (const pid of parentIdsToFetch) {
      this.taskApi
        .getTaskById(pid)
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroy$),
        )
        .subscribe((p) => {
          if (p) this.parentTaskTitles.set(pid, p.title);
        });
    }

    const companyIds = new Set<number>();
    const dealIds = new Set<number>();
    const leadIds = new Set<number>();
    const ownerIds = new Set<number>();

    for (const t of this.tasks) {
      if (t.intIdCompany && !this.companyNames.has(t.intIdCompany))
        companyIds.add(t.intIdCompany);
      if (t.intIdDeal && !this.dealNames.has(t.intIdDeal))
        dealIds.add(t.intIdDeal);
      if (t.intIdLead && !this.leadNames.has(t.intIdLead))
        leadIds.add(t.intIdLead);
      if (t.intIdUserOwner && !this.ownerNames.has(t.intIdUserOwner))
        ownerIds.add(t.intIdUserOwner);
    }

    if (
      companyIds.size === 0 &&
      dealIds.size === 0 &&
      leadIds.size === 0 &&
      ownerIds.size === 0
    )
      return;

    const calls = [
      ...Array.from(companyIds).map((id) =>
        this.empresaApi.getCompanyById(id).pipe(catchError(() => of(null))),
      ),
      ...Array.from(dealIds).map((id) =>
        this.dealApi
          .getDealById(id, businessId)
          .pipe(catchError(() => of(null))),
      ),
      ...Array.from(leadIds).map((id) =>
        this.leadApi
          .getLeadById(id, businessId)
          .pipe(catchError(() => of(null))),
      ),
    ];

    // Responsables: UNA sola llamada al directorio en modo `ids` (antes era un GET por id).
    // Devuelve también a los usuarios dados de baja, así una tarea antigua no pierde el nombre.
    if (ownerIds.size > 0) {
      this.userDirectory
        .fetchUsersByIds([...ownerIds])
        .pipe(
          catchError(() => of([])),
          takeUntil(this.destroy$),
        )
        .subscribe((users) => {
          for (const u of users) {
            this.ownerNames.set(
              u.id,
              `${directoryUserLabel(u)}${u.isActive === false ? ' (inactivo)' : ''}`,
            );
          }
        });
    }

    if (calls.length === 0) return;

    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          let i = 0;
          for (const id of companyIds) {
            const c = results[i++] as {
              id?: number;
              commercialName?: string;
            } | null;
            if (c?.commercialName) this.companyNames.set(id, c.commercialName);
          }
          for (const id of dealIds) {
            const d = results[i++] as { id?: number; name?: string } | null;
            if (d?.name) this.dealNames.set(id, d.name);
          }
          for (const id of leadIds) {
            const l = results[i++] as { id?: number; name?: string } | null;
            if (l?.name) this.leadNames.set(id, l.name);
          }
        },
      });
  }

  // ===== Nombres resueltos (con fallback al ID) =====
  companyName(id?: number): string | null {
    if (id == null) return null;
    return this.companyNames.get(id) ?? `Empresa #${id}`;
  }
  dealName(id?: number): string | null {
    if (id == null) return null;
    return this.dealNames.get(id) ?? `Negocio #${id}`;
  }
  leadName(id?: number): string | null {
    if (id == null) return null;
    return this.leadNames.get(id) ?? `Lead #${id}`;
  }

  getOwnerName(id?: number): string {
    if (id == null) return 'Sin asignar';
    return this.ownerNames.get(id) ?? `Usuario #${id}`;
  }

  /** ¿La tarea tiene alguna asociación (empresa/deal/lead)? */
  hasAnyAssociation(t: TaskResponse): boolean {
    return !!(t.intIdCompany || t.intIdDeal || t.intIdLead);
  }

  onFilterChanged(event: TaskFilterEvent): void {
    this.currentFilter = event.request;
    this.quickFilter = event.quickFilter;
    this.globalPage = 0;
    this.loadTasks();
  }

  // ===== Paginación =====

  changePage(p: number): void {
    if (p >= 0 && p < this.globalTotalPages) {
      this.globalPage = p;
      this.loadTasks();
    }
  }

  onPageSizeChange(): void {
    this.globalPage = 0;
    this.loadTasks();
  }

  // ===== Selección =====

  toggleOne(task: TaskResponse): void {
    if (task.id == null) return;
    const selected = !task.selected;
    this.setTaskSelection(task, selected);
    this.refreshAllSelected();
  }

  toggleAll(): void {
    this.allSelected = !this.allSelected;
    this.selectedTasksMap.clear();

    this.tasks.forEach((task) => {
      this.setTaskSelection(task, this.allSelected);
    });
  }

  private setTaskSelection(task: TaskResponse, selected: boolean): void {
    task.selected = selected;

    if (selected) {
      this.selectedTasksMap.set(task.id, task);
    } else {
      this.selectedTasksMap.delete(task.id);
    }

    task.subtasks?.forEach((subtask) =>
      this.setTaskSelection(subtask, selected),
    );
  }

  private refreshAllSelected(): void {
    this.allSelected =
      this.tasks.length > 0 &&
      this.tasks.every((t) => t.id != null && this.selectedTasksMap.has(t.id));
  }

  // Nuevo: para la validación de reasignación
  private getSelectedTaskOwners(): Set<number> {
    const owners = new Set<number>();
    this.selectedTasksMap.forEach((task) => {
      if (task.intIdUserOwner != null) {
        owners.add(task.intIdUserOwner);
      }
    });
    return owners;
  }

  clearSelection(): void {
    this.selectedTasksMap.clear();
    this.tasks.forEach((t) => (t.selected = false));
    this.allSelected = false;
  }

  // ===== Acciones de tarea individual =====

  nuevaTarea(): void {
    this.drawerService.open();
  }

  editarTarea(id: number): void {
    this.drawerService.open({ taskId: id });
  }

  abrirDetalle(id: number): void {
    this.detailTaskId = id;
    this.showDetailModal = true;
  }

  cerrarDetalle(): void {
    this.showDetailModal = false;
    this.detailTaskId = undefined;
    // Limpiar ?openTask para poder reabrir la misma tarea desde la notificación.
    if (this.route.snapshot.queryParamMap.has('openTask')) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openTask: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  onDetailChanged(): void {
    this.loadTasks();
  }

  async eliminarTarea(t: TaskResponse): Promise<void> {
    if (t.id == null) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar tarea',
      message: `¿Eliminar la tarea "${t.title}"? Esta acción no se puede deshacer.`,
    });
    if (!ok) return;
    this.taskApi
      .deleteTask(t.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadTasks();
          this.toast.deleted('Tarea');
          this.drawerService.notifySaved({ id: t.id } as any); // Notificamos para limpiar otras vistas
        },
        error: () => {
          this.toast.error({
            title: 'Error',
            message: 'No se pudo eliminar la tarea.',
          });
        },
      });
  }

  /** INC 35 y 36 — ¿la tarea ya está completada (estado terminal completado o finalizado)? */
  isFinished(t: TaskResponse): boolean {
    const statusCompleted = this.statuses.find(
      (status) => t.idTaskStatus === status.id,
    );

    if (
      statusCompleted?.isCompletedTerminal ||
      statusCompleted?.isCancelledTerminal
    ) {
      return true;
    }
    return false;
  }

  /** INC 26 — abre el prompt de completar con nota final opcional. */
  abrirCompletarDialog(t: TaskResponse): void {
    if (t.id == null) return;
    this.completeTargetTaskId = t.id;
    this.completeTargetTitle = t.title;
    this.completionNotes = '';
    this.showCompleteDialog = true;
  }

  closeCompleteDialog(): void {
    this.showCompleteDialog = false;
    this.completeTargetTaskId = null;
    this.completeTargetTitle = '';
    this.completionNotes = '';
  }

  /** INC 26 — confirma el completado enviando la nota opcional como completionNotes. */
  aplicarCompletar(): void {
    if (this.completeTargetTaskId == null) return;
    const id = this.completeTargetTaskId;
    this.taskApi
      .completeTask(id, {
        completionNotes: this.completionNotes?.trim() || undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.closeCompleteDialog();
          this.loadTasks();
          this.drawerService.notifySaved(updatedTask);
          this.toast.success('Tarea completada');
        },
        // INC 28 (subtareas pendientes) / INC 35 (ya completada): mostrar el message del backend en toast.
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo completar la tarea.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  posponer1Dia(t: TaskResponse): void {
    if (t.id == null) return;
    const due = new Date();
    due.setDate(due.getDate() + 1);
    this.taskApi
      .snoozeTask(t.id, {
        newDueDate: due.toISOString(),
        presetKey: 'TOMORROW',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.loadTasks();
          this.drawerService.notifySaved(updatedTask);
        },
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo posponer la tarea.',
          );
          this.confirmDialog.alert({
            title: 'Error',
            message: msg,
            variant: 'danger',
          });
        },
      });
  }

  async reabrir(t: TaskResponse): Promise<void> {
    if (t.id == null) return;
    const reason = await this.confirmDialog.prompt({
      title: 'Reabrir tarea',
      message: 'Indica el motivo por el cual se reabre esta tarea.',
      placeholder: 'Ej. requisito adicional del cliente',
      inputType: 'textarea',
      confirmText: 'Reabrir',
    });
    if (!reason) return;
    this.taskApi
      .reopenTask(t.id, { reason })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.loadTasks();
          this.drawerService.notifySaved(updatedTask);
        },
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo reabrir la tarea.',
          );
          this.confirmDialog.alert({
            title: 'Error',
            message: msg,
            variant: 'danger',
          });
        },
      });
  }

  /** Abre el dialog de cancelación apuntando a una sola tarea (reutiliza el markup del dialog bulk). */
  cancelarTarea(t: TaskResponse): void {
    if (t.id == null) return;
    this.cancelTargetTaskId = t.id;
    this.cancelTargetTitle = t.title;
    this.bulkCancelReasonId = this.cancelReasons[0]?.id;
    this.bulkCancelNotes = '';
    this.showCancelDialog = true;
  }

  private applySingleCancel(): void {
    if (this.cancelTargetTaskId == null || !this.bulkCancelReasonId) return;
    this.taskApi
      .cancelTask(this.cancelTargetTaskId, {
        idTaskCancelReason: this.bulkCancelReasonId,
        cancelNotes: this.bulkCancelNotes || undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.showCancelDialog = false;
          this.cancelTargetTaskId = null;
          this.cancelTargetTitle = '';
          this.loadTasks();
          this.drawerService.notifySaved(updatedTask);
          this.toast.success('Tarea cancelada');
        },
        // INC 36 — si ya está cancelada el backend responde 400; mostrar el message en toast.
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo cancelar la tarea.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  // ===== INC 30 — Reasignación individual con motivo =====

  /** Abre el diálogo de reasignación apuntando a una sola tarea (reutiliza el markup del diálogo masivo). */
  reasignarTarea(t: TaskResponse): void {
    if (t.id == null) return;
    this.reassignTargetTaskId = t.id;
    this.reassignTargetTitle = t.title;
    this.bulkNewOwner = null;
    this.bulkReassignReason = '';
    this.reassignWarningMessage = null;
    this.showReassignDialog = true;
  }

  private applySingleReassign(): void {
    if (this.reassignTargetTaskId == null || !this.bulkNewOwner) return;
    this.taskApi
      .reassignTask(this.reassignTargetTaskId, {
        newOwnerId: this.bulkNewOwner.id,
        reason: this.bulkReassignReason?.trim() || undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.closeReassignDialog();
          this.loadTasks();
          this.drawerService.notifySaved(updatedTask);
          this.toast.success('Tarea reasignada');
        },
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo reasignar la tarea.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  closeReassignDialog(): void {
    this.showReassignDialog = false;
    this.reassignTargetTaskId = null;
    this.reassignTargetTitle = '';
    this.bulkNewOwner = null;
    this.bulkReassignReason = '';
    this.reassignWarningMessage = null;
  }

  // ===== INC 34 — Cambiar a cualquier estado desde acciones rápidas =====

  toggleStatusMenu(t: TaskResponse, ev?: Event): void {
    ev?.stopPropagation();
    if (t.id == null) return;

    const target = ev?.currentTarget as HTMLElement | undefined;

    if (this.statusMenuTaskId === t.id) {
      this.statusMenuTaskId = null;
      return;
    }

    this.statusMenuTaskId = t.id;

    if (target) {
      const rect = target.getBoundingClientRect();
      const left = Math.min(rect.left, window.innerWidth - 208);
      const top = Math.min(rect.bottom + 6, window.innerHeight - 260);
      this.statusMenuPosition = { top, left };
    } else {
      this.statusMenuPosition = { top: 80, left: 20 };
    }
  }

  closeStatusMenu(): void {
    this.statusMenuTaskId = null;
  }

  /** Estados destino válidos para el dropdown (activos, distintos del estado actual de la tarea). */
  statusOptionsFor(t: TaskResponse): TaskStatusResponse[] {
    const currentId = t.status?.id ?? t.idTaskStatus;
    return this.statuses.filter((s) => s.id !== currentId);
  }

  cambiarEstado(t: TaskResponse, status: TaskStatusResponse): void {
    this.closeStatusMenu();
    if (t.id == null) return;
    if (status.isFinal && status.isCancelledTerminal) {
      this.cancelarTarea(t);
      return;
    } else if (status.isFinal && status.isCompletedTerminal) {
      this.abrirCompletarDialog(t);
      return;
    }
    this.taskApi
      .setTaskStatus(t.id, { idTaskStatus: status.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.loadTasks();
          this.drawerService.notifySaved(updatedTask);
          this.toast.updated('Tarea');
        },
        // INC 34 — transición inválida / ya en ese estado / estado final / cancelado: mostrar message del backend.
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo cambiar el estado de la tarea.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  // ===== Bulk actions =====

  bulkComplete(): void {
    if (this.selectedTasksMap.size === 0) return;
    this.runBulk({
      action: 'COMPLETE',
      taskIds: Array.from(this.selectedTasksMap.keys()),
    });
  }

  async bulkDelete(): Promise<void> {
    if (this.selectedTasksMap.size === 0) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: `Eliminar ${this.selectedTasksMap.size} tarea(s)`,
      message: `¿Estás seguro de que deseas eliminar ${this.selectedTasksMap.size} tarea(s)? Esta acción no se puede deshacer.`,
    });
    if (!ok) return;
    this.runBulk({
      action: 'DELETE',
      taskIds: Array.from(this.selectedTasksMap.keys()),
    });
  }

  openBulkSnooze(): void {
    if (this.selectedTasksMap.size === 0) return;
    this.bulkSnoozePreset = 'TOMORROW';
    const t = new Date();
    t.setDate(t.getDate() + 1);
    t.setHours(9, 0, 0, 0);
    this.bulkSnoozeDate = this.toLocalInput(t);
    this.showSnoozeDialog = true;
  }

  applyBulkSnooze(): void {
    if (!this.bulkSnoozeDate) return;
    const iso = new Date(this.bulkSnoozeDate).toISOString();
    this.runBulk({
      action: 'SNOOZE',
      taskIds: Array.from(this.selectedTasksMap.keys()),
      newDueDate: iso,
      presetKey: this.bulkSnoozePreset,
    });
    this.showSnoozeDialog = false;
  }

  // Nuevo: Métodos para la reasignación masiva
  openBulkReassign(): void {
    if (this.selectedTasksMap.size === 0) return;
    this.reassignTargetTaskId = null; // modo masivo
    this.reassignTargetTitle = '';
    this.bulkNewOwner = null;
    this.bulkReassignReason = '';
    this.reassignWarningMessage = null;
    this.showReassignDialog = true;
  }

  /** Punto de entrada del diálogo de reasignación: decide single o bulk según reassignTargetTaskId. */
  applyReassignDialog(): void {
    if (this.reassignTargetTaskId != null) {
      this.applySingleReassign();
      return;
    }
    this.applyBulkReassign();
  }

  onBulkOwnerChange(opt: PickerOption | null): void {
    this.bulkNewOwner = opt;
    this.reassignWarningMessage = null; // Reset warning on new selection

    // En modo single la validación de "todas mismo dueño" no aplica.
    if (this.reassignTargetTaskId != null) return;

    if (opt && this.selectedTasksMap.size > 0) {
      const allSameOwner = Array.from(this.selectedTasksMap.values()).every(
        (task) => task.intIdUserOwner === opt.id,
      );
      if (allSameOwner) {
        this.reassignWarningMessage =
          'El usuario seleccionado ya es el responsable de todas las tareas elegidas.';
      }
    }
  }

  applyBulkReassign(): void {
    if (
      !this.bulkNewOwner ||
      this.selectedTasksMap.size === 0 ||
      this.reassignWarningMessage
    )
      return;

    this.runBulk({
      action: 'REASSIGN',
      taskIds: Array.from(this.selectedTasksMap.keys()),
      newOwnerId: this.bulkNewOwner.id,
      reassignReason: this.bulkReassignReason?.trim() || undefined, // INC 30
    });
    this.closeReassignDialog();
  }

  openBulkCancel(): void {
    if (this.selectedTasksMap.size === 0) return;
    this.cancelTargetTaskId = null;
    this.cancelTargetTitle = '';
    this.bulkCancelReasonId = this.cancelReasons[0]?.id;
    this.bulkCancelNotes = '';
    this.showCancelDialog = true;
  }

  /** Punto de entrada del dialog: decide entre cancelación single o bulk según `cancelTargetTaskId`. */
  applyCancelDialog(): void {
    if (this.cancelTargetTaskId != null) {
      this.applySingleCancel();
      return;
    }
    if (!this.bulkCancelReasonId) return;
    this.runBulk({
      action: 'CANCEL',
      taskIds: Array.from(this.selectedTasksMap.keys()),
      cancelReasonId: this.bulkCancelReasonId,
      notes: this.bulkCancelNotes || undefined,
    });
    this.showCancelDialog = false;
  }

  closeCancelDialog(): void {
    this.showCancelDialog = false;
    this.cancelTargetTaskId = null;
    this.cancelTargetTitle = '';
  }

  applySnoozePreset(p: 'TOMORROW' | 'IN_3_DAYS' | 'NEXT_WEEK'): void {
    this.bulkSnoozePreset = p;
    const d = new Date();
    d.setHours(9, 0, 0, 0);
    if (p === 'TOMORROW') d.setDate(d.getDate() + 1);
    else if (p === 'IN_3_DAYS') d.setDate(d.getDate() + 3);
    else if (p === 'NEXT_WEEK') d.setDate(d.getDate() + 7);
    this.bulkSnoozeDate = this.toLocalInput(d);
  }

  private runBulk(req: TaskBulkRequest): void {
    this.loading = true;
    this.bulkApi
      .bulk(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          const hasFailures = res.totalFailed > 0;
          const friendlyAction = this.getFriendlyActionName(req.action);
          const failedDetail = hasFailures
            ? `\n${res.totalFailed} con error: ${res.failed.map((f) => `#${f.id} (${this.taskErrorService.getFriendlyMessage({ error: { code: f.reason } })})`).join(', ')}`
            : '';

          const successMsg =
            res.totalSucceeded === 1
              ? '1 tarea procesada'
              : `${res.totalSucceeded} tareas procesadas`;
          this.confirmDialog.alert({
            title: hasFailures
              ? 'Operación con errores'
              : 'Operación completada',
            message: `${friendlyAction}: ${successMsg} con éxito.${failedDetail}`,
            variant: hasFailures ? 'warning' : 'success',
          });
          this.clearSelection();
          this.loadTasks();
          this.drawerService.notifySaved({} as any); // Sincronización global tras operación masiva
        },
        error: (err: any) => {
          // Combinamos ambos manejadores de error
          this.loading = false;
          const friendlyAction = this.getFriendlyActionName(req.action);
          // Intentamos usar el mensaje de error específico del backend, si no, un mensaje genérico.
          const errorMessage = this.taskErrorService.getFriendlyMessage(
            err,
            `No se pudo ${friendlyAction.toLowerCase()} las tareas. Falló la operación masiva.`,
          );
          this.confirmDialog.alert({
            title: 'Error en la operación masiva',
            message: errorMessage,
            variant: 'danger',
          });
          this.clearSelection(); // Limpiar selección incluso si hay error general
        },
      });
  }

  private getFriendlyActionName(action: TaskBulkAction): string {
    return this.friendlyActionNames[action] || action;
  }

  // ===== Navegación =====

  vistaLista(): void {
    this.router.navigate(['/tareas']);
  }
  vistaKanban(): void {
    this.router.navigate(['/tareas/kamban']);
  }
  vistaCalendario(): void {
    this.router.navigate(['/tareas/calendario']);
  }

  // ===== Helpers =====

  trackById(_: number, t: TaskResponse): number {
    return t.id ?? 0;
  }

  isOverdue(t: TaskResponse): boolean {
    if (!t.datetimeDue || t.status?.isFinal) return false;
    return new Date(t.datetimeDue) < new Date();
  }

  formatDue(t: TaskResponse): string {
    if (!t.datetimeDue) return 'Sin fecha';
    const d = new Date(t.datetimeDue);
    if (isNaN(d.getTime())) return 'Sin fecha';
    return d.toLocaleString();
  }

  priorityVariant(
    t: TaskResponse,
  ): 'error' | 'warning' | 'info' | 'success' | 'light' {
    const lvl = t.priority?.level;
    if (lvl === 1) return 'error';
    if (lvl === 2) return 'warning';
    if (lvl === 3) return 'info';
    if (lvl === 4) return 'light';
    return 'light';
  }

  statusVariant(
    t: TaskResponse,
  ): 'error' | 'warning' | 'info' | 'success' | 'light' {
    if (t.status?.isFinal) {
      return t.status?.name === 'CANCELLED' ? 'error' : 'success';
    }
    return 'info';
  }

  /**
   * Helpers para resolver catálogos por id si el backend no devuelve el objeto anidado.
   * Devuelven el objeto completo (con label/name/color) o undefined.
   */
  resolveType(t: TaskResponse): TaskTypeResponse | undefined {
    if (t.type?.label) return t.type;
    const id = t.type?.id ?? t.idTaskType;
    return id != null ? this.types.find((x) => x.id === id) : undefined;
  }
  resolveStatus(t: TaskResponse): TaskStatusResponse | undefined {
    if (t.status?.label) return t.status;
    const id = t.status?.id ?? t.idTaskStatus;
    return id != null ? this.statuses.find((x) => x.id === id) : undefined;
  }
  resolvePriority(t: TaskResponse): TaskPriorityResponse | undefined {
    if (t.priority?.label) return t.priority;
    const id = t.priority?.id ?? t.idTaskPriority;
    return id != null ? this.priorities.find((x) => x.id === id) : undefined;
  }
  resolveCategory(t: TaskResponse): TaskCategoryResponse | undefined {
    if (t.category?.name) return t.category;
    const id = t.category?.id ?? t.idTaskCategory;
    return id != null ? this.categories.find((x) => x.id === id) : undefined;
  }

  subtaskProgress(t: TaskResponse): string {
    if (!t.subtaskCount) return '';
    return `${t.subtaskCompletedCount ?? 0}/${t.subtaskCount}`;
  }

  // ===== INC 12 — jerarquía visual de subtareas =====

  /** ¿La tarea es una subtarea (tiene padre)? */
  isSubtask(t: TaskResponse): boolean {
    if (t.idParentTask) {
      t.subtasks = this.tasks.filter(
        (subtask) => subtask.idParentTask === t.id,
      );
    }
    return t.idParentTask != null;
  }

  /** ¿La tarea es padre de otras (tiene subtareas)? */
  hasSubtasks(t: TaskResponse): boolean {
    return !!t.subtaskCount && t.subtaskCount > 0;
  }

  /** Título de la tarea padre resuelto (para "Subtarea de …"); fallback al id. */
  parentTaskTitle(t: TaskResponse): string {
    if (t.idParentTask == null) return '';
    return (
      this.parentTaskTitles.get(t.idParentTask) ?? `Tarea #${t.idParentTask}`
    );
  }

  /** Abre el detalle de la tarea padre desde la etiqueta "Subtarea de …". */
  abrirPadre(t: TaskResponse, ev: Event): void {
    ev.stopPropagation();
    if (t.idParentTask != null) this.abrirDetalle(t.idParentTask);
  }

  private toLocalInput(d: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** Picker de responsable del listado — directorio unificado con búsqueda en servidor. */
  private searchUsersFn(
    term: string,
    _page: number,
  ): Observable<PickerSearchPage> {
    return this.userDirectory.searchUsers(term || '', 20).pipe(
      map(
        (list) =>
          ({
            items: list.map(
              (u) =>
                ({
                  id: u.id,
                  label: directoryUserLabel(u),
                  sublabel: u.mail,
                }) as PickerOption,
            ),
            totalPages: 1,
          }) as PickerSearchPage,
      ),
      catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
    );
  }

  private formatUserName(
    fullname?: string | null,
    lastname?: string | null,
  ): string {
    return `${fullname ?? ''}${lastname ? ' ' + lastname : ''}`.trim() || '—';
  }
}
